// Hooks
import { useReviewRetrospectiveClaim } from "containers/ReviewRetrospectiveClaim/useReviewRetrospectiveClaim";

// Components
import { Head } from "components/molecules/Head/Head";
import PageTitle from "components/molecules/PageTitle/PageTitle";
import Button from "components/atoms/Button/Button";
import FileUpload from "components/atoms/FileUpload/FileUpload";
import ReviewClaimDetails from "components/organisms/ReviewClaimDetails/ReviewClaimDetails";
import Spinner from "components/atoms/Spinner/Spinner";
import CollapsiblePanel from "components/atoms/CollapsiblePanel/CollapsiblePanel";

// Styles
import styles from "containers/ReviewRetrospectiveClaim/reviewRetrospectiveClaim.module.css";

// Constants & Utils
import { PageTitles, REVIEW_RETRO_CLAIM as Constants, RETRO_CLAIM } from "constants/global";

// ANT Modules
import { Image } from "antd";

// Type Definitions
import { RetroClaimListItem } from "services/api/types";

export type ReviewRetrospectiveClaimProps = {
	retroClaimItem?: RetroClaimListItem;
};

const buttonTypeClass = (isEnabled: boolean) => {
	if (isEnabled) {
		return "active";
	}
	return "disabled";
};

const ReviewRetrospectiveClaim = (): JSX.Element => {
	const componentProps = useReviewRetrospectiveClaim();

	const {
		reviewClaimDetails,
		disableFields,
		isNextEnabled,
		fieldErrors,
		storesDataList,
		outletCode,
		loading,
		generateHeader,
		generateCols,
		generateData,
		generateModificationHistoryHeader,
		generateModificationHistoryCols,
		generateModificationHistoryData,
		filledDataValidation,
		outletName,
		terminalsList,
		filteredOutletsList,
		terminal,
		form,
		handleTerminalSelect,
		handleInputChange,
		handleOnDateChange,
		handleOnDateOk,
		handleUpload,
		validateFields,
		handleApprove,
		handleReject,
		handleUpdateClaim,
		goBack,
		handleOutletSearch,
		handleOutletSelect,
		showButtons,
		isOutletInactive,
		claimStatus
	} = componentProps;

	const handlerFunction = (enabled: boolean, type: string) => {
		if (enabled) {
			switch (type) {
				case RETRO_CLAIM.PAGE_STATUS.APPROVE:
					return handleApprove;
				case RETRO_CLAIM.PAGE_STATUS.REJECT:
					return handleReject;
			}
		}
		return undefined;
	};

	return (
		<div className={styles?.root}>
			{loading && <Spinner type="transparent" />}
			<Head>{PageTitles?.REVIEW_RETROSPECTIVE_CLAIM}</Head>
			<PageTitle showBackIcon={true} title={Constants.PAGE_TITLE} subTitle={Constants.PAGE_SUBTITLE} goBack={goBack} />
			<div className={styles?.main_wrapper}>
				<div className="md:flex">
					<div className={`${styles?.image_wrapper}`}>
						{!disableFields?.receipt_imgurl && <FileUpload imageURL={reviewClaimDetails?.receipt_imgurl} handleUpload={handleUpload} />}
						{disableFields?.receipt_imgurl && (
							<div>
								<Image src={reviewClaimDetails?.receipt_imgurl} alt="receipt_img_base64" preview={false} className="flex justify-center items-center h-[33rem]" />
							</div>
						)}
					</div>
					<div className={styles?.review_claim_form_wrapper}>
						<ReviewClaimDetails
							reviewClaimDetails={reviewClaimDetails}
							handleInputChange={handleInputChange}
							validateFields={validateFields}
							handleDateChange={handleOnDateChange}
							handleDateOk={handleOnDateOk}
							outletCode={outletCode}
							handleOutletSearch={handleOutletSearch}
							handleOutletSelect={handleOutletSelect}
							handleTerminalSelect={handleTerminalSelect}
							outletsList={storesDataList}
							fieldErrors={fieldErrors}
							disableFields={disableFields}
							filledDataValidation={filledDataValidation}
							outletName={outletName}
							form={form}
							terminal={terminal}
							terminalsList={terminalsList}
							filteredOutletsList={filteredOutletsList}
							isOutletInactive={isOutletInactive}
							claimStatus={claimStatus}
						/>
					</div>
				</div>

				<div className={styles?.bottom_wrapper}>
					<div className="flex justify-end">
						{showButtons.updateClaim && (
							<>
								<Button {...Constants.BUTTON_LABELS.UPDATE_CLAIM} type={buttonTypeClass(isNextEnabled)} onClick={handleUpdateClaim} />
								<span className="mx-[0.3125rem]"></span>
							</>
						)}
						{showButtons.cancel && (
							<>
								<Button {...Constants.BUTTON_LABELS.CANCEL} type={buttonTypeClass(true)} onClick={goBack} />
								<span className="mx-[0.3125rem]"></span>
							</>
						)}

						{showButtons.approve && (
							<>
								<Button
									{...Constants.BUTTON_LABELS.APPROVE}
									type={buttonTypeClass(isNextEnabled && !disableFields?.approve_btn)}
									onClick={handlerFunction(isNextEnabled && !disableFields?.approve_btn, RETRO_CLAIM.PAGE_STATUS.APPROVE)}
								/>
								<span className="mx-[0.3125rem]"></span>
							</>
						)}
						{showButtons.reject && (
							<Button {...Constants.BUTTON_LABELS.REJECT} type={buttonTypeClass(isNextEnabled)} onClick={handlerFunction(isNextEnabled, RETRO_CLAIM.PAGE_STATUS.REJECT)} />
						)}
					</div>
				</div>
				<div className="mt-9">
					<CollapsiblePanel header={generateModificationHistoryHeader} columns={generateModificationHistoryCols} data={generateModificationHistoryData} />
				</div>

				<div className="mt-4">
					<CollapsiblePanel header={generateHeader} columns={generateCols} data={generateData} />
				</div>
			</div>
		</div>
	);
};

export default ReviewRetrospectiveClaim;
